import React, { useState, useRef } from 'react'
import SpeechToText from './SpeechToText'
import {
  Box,
  Typography,
  Modal,
  TextField,
  Button,
  FormControl,
  Alert,
  CircularProgress,
  IconButton,
  FormLabel,
  Collapse,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Snackbar,
} from '@mui/material'
import CloseIcon from '@mui/icons-material/Close'
import {
  AutoAwesome,
  ExpandMore,
  ExpandLess,
  GetApp,
  PictureAsPdf,
  Description,
  Email,
} from '@mui/icons-material'

import { SponsorshipLetterData } from '../models/FundingNeeds'
import {
  generateSponsorLetter,
  emailSponsorshipLetter,
} from '@/api/fundingNeedsAPI'
import { UUID } from 'crypto'
import { useProfileRegenGuard } from '@/components/ProfileRegenBlockProvider'

interface GenerateSponsorshipLetterProps {
  isOpen: boolean
  onClose: () => void
  token: string
  needId: number
  orgUuid: UUID
  // onLetterGenerated?: (letterData: SponsorshipLetterData) => void
}

const GenerateSponsorshipLetter: React.FC<GenerateSponsorshipLetterProps> = ({
  isOpen,
  onClose,
  token,
  needId,
  orgUuid,
  // onLetterGenerated,
}) => {
  const guardRegen = useProfileRegenGuard()
  const [sponsorName, setSponsorName] = useState('')
  const [sponsorContactName, setSponsorContactName] = useState('')
  const [sponsorContact, setSponsorContact] = useState('')
  const [requestDetails, setRequestDetails] = useState('')
  const [eventDetails, setEventDetails] = useState('')
  const [timeline, setTimeline] = useState('')
  const [valueProposition, setValueProposition] = useState('')
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [clipboardError, setClipboardError] = useState<string | null>(null)
  const [generatedLetter, setGeneratedLetter] = useState<string>('')
  const [editingLetter, setEditingLetter] = useState(false)
  const [showInputs, setShowInputs] = useState(true)
  const [letterText, setLetterText] = useState('')
  const [editingInputs, setEditingInputs] = useState(false)
  const [showSuggestChanges, setShowSuggestChanges] = useState(false)
  const [suggestionFeedback, setSuggestionFeedback] = useState('')
  const [exportMenuAnchor, setExportMenuAnchor] = useState<null | HTMLElement>(
    null
  )
  const [showCopySuccess, setShowCopySuccess] = useState(false)
  const [showEmailError, setShowEmailError] = useState(false)
  const [showEmailSuccess, setShowEmailSuccess] = useState(false)
  const [emailing, setEmailing] = useState(false)
  const [showUnsavedWarning, setShowUnsavedWarning] = useState(false)
  const [showCloseConfirmation, setShowCloseConfirmation] = useState(false)
  const [showInputFormCancelConfirmation, setShowInputFormCancelConfirmation] =
    useState(false)
  const [
    showLetterEditCancelConfirmation,
    setShowLetterEditCancelConfirmation,
  ] = useState(false)
  const [hasBeenCopied, setHasBeenCopied] = useState(false)
  const [hasBeenEmailed, setHasBeenEmailed] = useState(false)
  const [lastExportedText, setLastExportedText] = useState('')
  const [showSuccessAlert, setShowSuccessAlert] = useState(false)
  const [successMessage, setSuccessMessage] = useState('')

  // Refs for scrolling to warnings and focus management
  const inputFormWarningRef = useRef<HTMLDivElement>(null)
  const closeWarningRef = useRef<HTMLDivElement>(null)
  const errorAlertRef = useRef<HTMLDivElement>(null)
  const successAlertRef = useRef<HTMLDivElement>(null)
  const unsavedWarningRef = useRef<HTMLDivElement>(null)
  const letterEditCancelWarningRef = useRef<HTMLDivElement>(null)
  const letterEditRef = useRef<HTMLInputElement>(null)

  // Helper function to set error and scroll to it
  const setErrorWithScroll = (errorMessage: string) => {
    setError(errorMessage)
    setTimeout(() => {
      if (errorAlertRef.current) {
        errorAlertRef.current.scrollIntoView({
          behavior: 'smooth',
          block: 'center',
        })
        errorAlertRef.current.focus()
      }
    }, 100)
  }

  // Helper function to show success alert and focus it
  const setSuccessWithFocus = (message: string) => {
    setSuccessMessage(message)
    setShowSuccessAlert(true)
    setTimeout(() => {
      if (successAlertRef.current) {
        successAlertRef.current.scrollIntoView({
          behavior: 'smooth',
          block: 'center',
        })
        successAlertRef.current.focus()
      }
    }, 100)
  }

  // Track saved inputs (separate from original generation)
  const [savedInputs, setSavedInputs] = useState({
    sponsorName: '',
    sponsorContactName: '',
    sponsorContact: '',
    requestDetails: '',
    eventDetails: '',
    timeline: '',
    valueProposition: '',
  })

  const shouldShowCloseConfirmation = () => {
    // Don't show confirmation if there's no generated letter
    if (!generatedLetter) return false

    // Check if letter hasn't been copied or emailed
    const hasNotBeenExported = !hasBeenCopied && !hasBeenEmailed

    // Check if there have been changes since last export
    const hasChangedSinceExport =
      letterText !== lastExportedText && lastExportedText !== ''

    return hasNotBeenExported || hasChangedSinceExport
  }

  const hasUnsavedInputs = () => {
    return (
      sponsorName.trim() !== '' ||
      sponsorContactName.trim() !== '' ||
      sponsorContact.trim() !== '' ||
      requestDetails.trim() !== '' ||
      eventDetails.trim() !== '' ||
      timeline.trim() !== '' ||
      valueProposition.trim() !== ''
    )
  }

  const handleInputFormCancel = () => {
    if (hasUnsavedInputs()) {
      setShowInputFormCancelConfirmation(true)
      // Scroll to the warning to make it visible and focus it
      setTimeout(() => {
        if (inputFormWarningRef.current) {
          inputFormWarningRef.current.scrollIntoView({
            behavior: 'smooth',
            block: 'center',
          })
          inputFormWarningRef.current.focus()
        }
      }, 100)
      return
    }
    handleConfirmedClose()
  }

  const handleClose = () => {
    // If we're in the input form and have unsaved inputs, show input form confirmation
    if (!generatedLetter && hasUnsavedInputs()) {
      setShowInputFormCancelConfirmation(true)
      // Scroll to the warning to make it visible and focus it
      setTimeout(() => {
        if (inputFormWarningRef.current) {
          inputFormWarningRef.current.scrollIntoView({
            behavior: 'smooth',
            block: 'center',
          })
          inputFormWarningRef.current.focus()
        }
      }, 100)
      return
    }

    // If we have a generated letter, check if we should show close confirmation
    if (shouldShowCloseConfirmation()) {
      setShowCloseConfirmation(true)
      // Scroll to the warning to make it visible and focus it
      setTimeout(() => {
        if (closeWarningRef.current) {
          closeWarningRef.current.scrollIntoView({
            behavior: 'smooth',
            block: 'center',
          })
          closeWarningRef.current.focus()
        }
      }, 100)
      return
    }

    handleConfirmedClose()
  }

  const handleConfirmedClose = () => {
    // Reset all state when closing
    setSponsorName('')
    setSponsorContactName('')
    setSponsorContact('')
    setRequestDetails('')
    setEventDetails('')
    setTimeline('')
    setValueProposition('')
    setGeneratedLetter('')
    setLetterText('')
    setEditingLetter(false)
    setShowInputs(true)
    setEditingInputs(false)
    setShowSuggestChanges(false)
    setSuggestionFeedback('')
    setExportMenuAnchor(null)
    setShowCopySuccess(false)
    setShowUnsavedWarning(false)
    setShowCloseConfirmation(false)
    setShowInputFormCancelConfirmation(false)
    setShowLetterEditCancelConfirmation(false)
    setHasBeenCopied(false)
    setHasBeenEmailed(false)
    setLastExportedText('')
    setError(null)
    setClipboardError(null)
    // Reset saved inputs
    const emptyInputs = {
      sponsorName: '',
      sponsorContactName: '',
      sponsorContact: '',
      requestDetails: '',
      eventDetails: '',
      timeline: '',
      valueProposition: '',
    }
    setSavedInputs(emptyInputs)
    onClose()
  }

  // Check if current inputs differ from saved inputs (for Update button)
  const hasUnsavedInputChanges = () => {
    return (
      sponsorName.trim() !== savedInputs.sponsorName ||
      sponsorContactName.trim() !== savedInputs.sponsorContactName ||
      sponsorContact.trim() !== savedInputs.sponsorContact ||
      requestDetails.trim() !== savedInputs.requestDetails ||
      eventDetails.trim() !== savedInputs.eventDetails ||
      timeline.trim() !== savedInputs.timeline ||
      valueProposition.trim() !== savedInputs.valueProposition
    )
  }

  const handleCancelInputEditing = () => {
    if (hasUnsavedInputChanges()) {
      setShowUnsavedWarning(true)
      // Focus the unsaved warning alert
      setTimeout(() => {
        if (unsavedWarningRef.current) {
          unsavedWarningRef.current.scrollIntoView({
            behavior: 'smooth',
            block: 'center',
          })
          unsavedWarningRef.current.focus()
        }
      }, 100)
      return
    }
    setEditingInputs(false)
  }

  const handleDiscardChanges = () => {
    // Revert to saved inputs
    setSponsorName(savedInputs.sponsorName)
    setSponsorContactName(savedInputs.sponsorContactName)
    setSponsorContact(savedInputs.sponsorContact)
    setRequestDetails(savedInputs.requestDetails)
    setEventDetails(savedInputs.eventDetails)
    setTimeline(savedInputs.timeline)
    setValueProposition(savedInputs.valueProposition)
    setShowUnsavedWarning(false)
    setEditingInputs(false)
  }

  const handleKeepEditing = () => {
    setShowUnsavedWarning(false)
  }

  // Check if letter text has changed from original
  const hasUnsavedLetterChanges = () => {
    return letterText !== generatedLetter
  }

  const handleCancelLetterEdit = () => {
    if (hasUnsavedLetterChanges()) {
      setShowLetterEditCancelConfirmation(true)
      // Focus the letter edit cancel warning alert
      setTimeout(() => {
        if (letterEditCancelWarningRef.current) {
          letterEditCancelWarningRef.current.scrollIntoView({
            behavior: 'smooth',
            block: 'center',
          })
          letterEditCancelWarningRef.current.focus()
        }
      }, 100)
      return
    }
    // No changes, just cancel
    setLetterText(generatedLetter)
    setEditingLetter(false)
  }

  const handleDiscardLetterChanges = () => {
    setLetterText(generatedLetter)
    setEditingLetter(false)
    setShowLetterEditCancelConfirmation(false)
  }

  const handleKeepEditingLetter = () => {
    setShowLetterEditCancelConfirmation(false)
  }

  const handleRegenerateWithChanges = async () => {
    setError(null)
    setIsSubmitting(true)

    try {
      const letterData: SponsorshipLetterData = {
        sponsor_name: sponsorName.trim(),
        sponsor_contact_name: sponsorContactName.trim(),
        sponsor_contact_details: sponsorContact.trim(),
        request_details: requestDetails.trim(),
        event_program_details: eventDetails.trim(),
        timeline_deadline: timeline.trim(),
        value_proposition: valueProposition.trim() || undefined,
        org_uuid: orgUuid,
      }

      const response = await generateSponsorLetter(token, needId, letterData)

      if (response.error) {
        setErrorWithScroll(response.error)
        setIsSubmitting(false)
        return
      }

      if (response.sponsor_letter) {
        setGeneratedLetter(response.sponsor_letter)
        setLetterText(response.sponsor_letter)
        setEditingInputs(false)
        // Update saved inputs
        const newInputs = {
          sponsorName: sponsorName.trim(),
          sponsorContactName: sponsorContactName.trim(),
          sponsorContact: sponsorContact.trim(),
          requestDetails: requestDetails.trim(),
          eventDetails: eventDetails.trim(),
          timeline: timeline.trim(),
          valueProposition: valueProposition.trim(),
        }
        setSavedInputs(newInputs)
        // Show success alert
        setSuccessWithFocus('Letter has been regenerated!')
      }

      setIsSubmitting(false)
    } catch (err) {
      if (guardRegen(err)) {
        setIsSubmitting(false)
        return
      }
      setErrorWithScroll('Failed to regenerate sponsorship letter')
      setIsSubmitting(false)
    }
  }

  const handleExportMenuOpen = (event: React.MouseEvent<HTMLButtonElement>) => {
    setExportMenuAnchor(event.currentTarget)
  }

  const handleExportMenuClose = () => {
    setExportMenuAnchor(null)
  }

  async function handleEmailLetter() {
    if (!generatedLetter || !token) {
      return
    }

    setShowEmailError(false)
    setShowEmailSuccess(false)
    setEmailing(true)

    try {
      const response = await emailSponsorshipLetter(
        token,
        needId,
        sponsorName,
        generatedLetter
      )

      if (response.error !== null) {
        setShowEmailError(true)
      }

      if (response.success) {
        setShowEmailError(false)
        setShowEmailSuccess(true)
        // Track that letter has been emailed and record the text that was emailed
        setHasBeenEmailed(true)
        setLastExportedText(generatedLetter)
        // Hide close confirmation since letter is now saved
        setShowCloseConfirmation(false)
      }
    } catch {
      setShowEmailError(true)
    } finally {
      setEmailing(false)
    }
  }

  const handleExportOption = async (
    type: 'copy' | 'pdf' | 'docx' | 'email'
  ) => {
    handleExportMenuClose()

    switch (type) {
      case 'copy':
        try {
          await navigator.clipboard.writeText(letterText)
          setShowCopySuccess(true)
          // Track that letter has been copied and record the text that was copied
          setHasBeenCopied(true)
          setLastExportedText(letterText)
          // Hide close confirmation since letter is now saved
          setShowCloseConfirmation(false)
        } catch (error) {
          // Fallback for browsers without clipboard API
          try {
            const textArea = document.createElement('textarea')
            textArea.value = letterText
            textArea.style.position = 'fixed'
            textArea.style.left = '-999999px'
            textArea.style.top = '-999999px'
            document.body.appendChild(textArea)
            textArea.focus()
            textArea.select()
            const successful = document.execCommand('copy')
            document.body.removeChild(textArea)

            if (successful) {
              setShowCopySuccess(true)
              // Track that letter has been copied and record the text that was copied
              setHasBeenCopied(true)
              setLastExportedText(letterText)
              // Hide close confirmation since letter is now saved
              setShowCloseConfirmation(false)
            } else {
              throw new Error('Fallback copy failed')
            }
          } catch (fallbackError) {
            // Show an error message to the user instead of false success
            setClipboardError(
              'Unable to copy to clipboard. Please select and copy the text manually.'
            )
          }
        }
        break
      case 'pdf':
        // TODO: Implement PDF export
        // console.log('Exporting as PDF...')
        break
      case 'docx':
        // TODO: Implement DOCX export
        // console.log('Exporting as DOCX...')
        break
      case 'email':
        handleEmailLetter()
        break
    }
  }

  if (!isOpen) {
    return null
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    if (
      !sponsorName.trim() ||
      !sponsorContactName.trim() ||
      !requestDetails.trim() ||
      !eventDetails.trim() ||
      !timeline.trim()
    ) {
      setErrorWithScroll('Please fill in all required fields')
      return
    }

    setError(null)
    setIsSubmitting(true)

    try {
      const letterData: SponsorshipLetterData = {
        sponsor_name: sponsorName.trim(),
        sponsor_contact_name: sponsorContactName.trim(),
        sponsor_contact_details: sponsorContact.trim(),
        request_details: requestDetails.trim(),
        event_program_details: eventDetails.trim(),
        timeline_deadline: timeline.trim(),
        value_proposition: valueProposition.trim() || undefined,
        org_uuid: orgUuid,
      }

      const response = await generateSponsorLetter(token, needId, letterData)

      if (response.error) {
        setErrorWithScroll(response.error)
        setIsSubmitting(false)
        return
      }

      if (response.sponsor_letter) {
        setGeneratedLetter(response.sponsor_letter)
        setLetterText(response.sponsor_letter)
        setShowInputs(false)
        setEditingLetter(false)
        // Store the input values
        const inputValues = {
          sponsorName: sponsorName.trim(),
          sponsorContactName: sponsorContactName.trim(),
          sponsorContact: sponsorContact.trim(),
          requestDetails: requestDetails.trim(),
          eventDetails: eventDetails.trim(),
          timeline: timeline.trim(),
          valueProposition: valueProposition.trim(),
        }
        setSavedInputs(inputValues)
        // Show success alert
        setSuccessWithFocus('Letter has been generated!')
      }

      setIsSubmitting(false)
    } catch (err) {
      if (guardRegen(err)) {
        setIsSubmitting(false)
        return
      }
      setErrorWithScroll('Failed to generate sponsorship letter')
      setIsSubmitting(false)
    }
  }

  return (
    <Modal
      open={isOpen}
      onClose={onClose}
      aria-labelledby="sponsorship-letter-modal-title"
      aria-describedby="sponsorship-letter-modal-description"
    >
      <Box
        sx={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: '800px',
          maxWidth: '90vw',
          bgcolor: 'background.paper',
          border: '2px solid #000',
          boxShadow: 24,
          px: 4,
          borderRadius: 2,
          pt: 3,
          pb: 3,
          maxHeight: '90vh',
          overflow: 'auto',
        }}
      >
        <IconButton
          aria-label="close"
          onClick={handleClose}
          disabled={isSubmitting}
          disableRipple
          sx={{
            position: 'absolute',
            right: 8,
            top: 8,
            color: (theme) => theme.palette.grey[500],
            '&:focus': {
              backgroundColor: '#e8e8e8',
            },
          }}
        >
          <CloseIcon />
        </IconButton>

        <Typography
          id="sponsorship-letter-modal-title"
          variant="h5"
          component="h2"
          sx={{ textAlign: 'center', mb: 1, fontWeight: 500 }}
        >
          Generate Sponsorship Letter
        </Typography>

        <Typography
          id="sponsorship-letter-modal-description"
          variant="body1"
          sx={{ mb: 4, color: 'text.secondary', textAlign: 'center' }}
        >
          {generatedLetter ? (
            <>
              Review the letter below, edit if needed, then copy it to use in
              your sponsorship request.
            </>
          ) : (
            'Provide the details below to generate a personalised sponsorship request letter.'
          )}
        </Typography>

        {error && (
          <Alert
            severity="error"
            sx={{ mb: 2 }}
            role="alert"
            aria-live="assertive"
            ref={errorAlertRef}
            tabIndex={-1}
          >
            <Typography variant="subtitle2">Error generating letter</Typography>
            <Typography variant="body2">{error}</Typography>
          </Alert>
        )}

        {showSuccessAlert && (
          <Alert
            severity="success"
            sx={{ mb: 2 }}
            role="alert"
            aria-live="assertive"
            ref={successAlertRef}
            tabIndex={-1}
            onClose={() => setShowSuccessAlert(false)}
          >
            <Typography variant="body2">{successMessage}</Typography>
          </Alert>
        )}

        {clipboardError && (
          <Alert
            severity="warning"
            sx={{ mb: 2 }}
            role="alert"
            aria-live="assertive"
            onClose={() => setClipboardError(null)}
            tabIndex={-1}
          >
            <Typography variant="subtitle2">Clipboard Error</Typography>
            <Typography variant="body2">{clipboardError}</Typography>
          </Alert>
        )}

        {/* Generated Letter Section */}
        {generatedLetter && (
          <Box sx={{ mb: 3 }}>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                mb: 2,
              }}
            >
              <Button
                variant="text"
                onClick={() => setShowInputs(!showInputs)}
                disableRipple
                startIcon={showInputs ? <ExpandLess /> : <ExpandMore />}
                sx={{
                  textTransform: 'none',
                  color: '#1C3045',
                  '&:focus': {
                    backgroundColor: '#e8e8e8',
                  },
                }}
              >
                {showInputs ? 'Hide' : 'Show'} Request Details
              </Button>
              {showInputs && (
                <Button
                  variant="text"
                  onClick={() =>
                    editingInputs
                      ? handleCancelInputEditing()
                      : setEditingInputs(true)
                  }
                  disableRipple
                  sx={{
                    textTransform: 'none',
                    color: '#1C3045',
                    '&:focus': {
                      backgroundColor: '#e8e8e8',
                    },
                  }}
                >
                  {editingInputs ? 'Cancel Editing' : 'Edit Details'}
                </Button>
              )}
            </Box>

            {/* Unsaved Changes Warning */}
            {showUnsavedWarning && (
              <Alert
                severity="warning"
                sx={{ mb: 2 }}
                role="alert"
                aria-live="assertive"
                tabIndex={-1}
                ref={unsavedWarningRef}
              >
                <Typography variant="body2" sx={{ mb: 2 }}>
                  You have unsaved changes to your request details that haven't
                  been applied to your letter yet. Click "Continue" to discard
                  changes or "Go Back" to keep editing.
                </Typography>
                <Typography variant="body2" sx={{ mb: 2 }}>
                  If you'd like to regenerate the letter with the updated
                  details, click "Regenerate with Changes".
                </Typography>
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <Button
                    size="small"
                    variant="outlined"
                    onClick={handleKeepEditing}
                    disableRipple
                    sx={{
                      textTransform: 'none',
                      '&:focus': {
                        backgroundColor: '#e8e8e8',
                      },
                    }}
                  >
                    Go Back
                  </Button>
                  <Button
                    size="small"
                    variant="contained"
                    onClick={handleDiscardChanges}
                    disableRipple
                    sx={{
                      textTransform: 'none',
                    }}
                  >
                    Continue
                  </Button>
                </Box>
              </Alert>
            )}

            {/* Close Confirmation Warning */}
            {showCloseConfirmation && (
              <Alert
                severity="warning"
                sx={{ mb: 2 }}
                ref={closeWarningRef}
                role="alert"
                aria-live="assertive"
                tabIndex={-1}
              >
                <Typography variant="body2" sx={{ mb: 2 }}>
                  You haven't copied or emailed a copy of this letter yet, or
                  you've made changes since the last export. Are you sure you
                  want to close?
                </Typography>
                <Typography variant="body2" sx={{ mb: 2 }}>
                  If you'd like to email yourself a copy of this letter or copy
                  the text, click the 'Export Letter' button.
                </Typography>
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <Button
                    size="small"
                    variant="outlined"
                    onClick={() => setShowCloseConfirmation(false)}
                    disableRipple
                    sx={{
                      textTransform: 'none',
                      '&:focus': {
                        backgroundColor: '#e8e8e8',
                      },
                    }}
                  >
                    Keep Letter Open
                  </Button>
                  <Button
                    size="small"
                    variant="contained"
                    onClick={handleConfirmedClose}
                    disableRipple
                    sx={{
                      textTransform: 'none',
                      '&:focus': {
                        backgroundColor: '#e8e8e8',
                      },
                    }}
                  >
                    Close Anyway
                  </Button>
                </Box>
              </Alert>
            )}

            <Collapse in={showInputs}>
              <Box
                sx={{
                  mb: 2,
                  p: 2,
                  backgroundColor: editingInputs ? 'white' : '#f5f5f5',
                  borderRadius: 1,
                  border: editingInputs ? '1px solid #ddd' : 'none',
                }}
              >
                {editingInputs ? (
                  // Editable input fields
                  <Box
                    sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}
                  >
                    <TextField
                      label="Sponsor Name"
                      value={sponsorName}
                      onChange={(e) => setSponsorName(e.target.value)}
                      size="small"
                      fullWidth
                      autoComplete="off"
                      aria-label="Sponsor name - edit mode"
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          backgroundColor: 'white',
                        },
                      }}
                    />
                    <TextField
                      label="Sponsor Contact Name"
                      value={sponsorContactName}
                      onChange={(e) => setSponsorContactName(e.target.value)}
                      size="small"
                      multiline
                      rows={2}
                      fullWidth
                      autoComplete="off"
                      aria-label="Sponsor contact name - edit mode"
                    />
                    <TextField
                      label="Sponsor Contact Details"
                      value={sponsorContact}
                      onChange={(e) => setSponsorContact(e.target.value)}
                      size="small"
                      multiline
                      rows={2}
                      fullWidth
                      autoComplete="off"
                      aria-label="Sponsor contact details - edit mode"
                    />
                    <TextField
                      label="Request Details"
                      value={requestDetails}
                      onChange={(e) => setRequestDetails(e.target.value)}
                      size="small"
                      multiline
                      rows={3}
                      fullWidth
                      autoComplete="off"
                      aria-label="Request details - edit mode"
                      sx={{
                        '& textarea': {
                          resize: 'vertical',
                        },
                      }}
                    />
                    <TextField
                      label="Event Details"
                      value={eventDetails}
                      onChange={(e) => setEventDetails(e.target.value)}
                      size="small"
                      multiline
                      rows={3}
                      fullWidth
                      autoComplete="off"
                      aria-label="Event details - edit mode"
                      sx={{
                        '& textarea': {
                          resize: 'vertical',
                        },
                      }}
                    />
                    <TextField
                      label="Timeline"
                      value={timeline}
                      onChange={(e) => setTimeline(e.target.value)}
                      size="small"
                      fullWidth
                      autoComplete="off"
                      aria-label="Timeline - edit mode"
                    />
                    <TextField
                      label="Value Proposition (Optional)"
                      value={valueProposition}
                      onChange={(e) => setValueProposition(e.target.value)}
                      size="small"
                      multiline
                      rows={2}
                      fullWidth
                      autoComplete="off"
                      aria-label="Value proposition - edit mode"
                      sx={{
                        '& textarea': {
                          resize: 'vertical',
                        },
                      }}
                    />

                    <Box
                      sx={{
                        display: 'flex',
                        gap: 1,
                        justifyContent: 'flex-end',
                        mt: 2,
                      }}
                    >
                      <Button
                        variant="contained"
                        onClick={handleRegenerateWithChanges}
                        disabled={isSubmitting || !hasUnsavedInputChanges()}
                        disableRipple
                        startIcon={
                          isSubmitting ? (
                            <CircularProgress
                              size={20}
                              sx={{ color: 'inherit' }}
                            />
                          ) : (
                            <AutoAwesome />
                          )
                        }
                        sx={{
                          bgcolor: '#1C3045',
                          textTransform: 'none',
                        }}
                      >
                        {isSubmitting
                          ? 'Regenerating...'
                          : 'Regenerate with Changes'}
                      </Button>
                    </Box>
                  </Box>
                ) : (
                  // Read-only view
                  <Box>
                    <Typography variant="body2" sx={{ mb: 1 }}>
                      <span style={{ fontWeight: 600 }}>Sponsor:</span>{' '}
                      {sponsorName}
                    </Typography>
                    <Typography variant="body2" sx={{ mb: 1 }}>
                      <span style={{ fontWeight: 600 }}>Contact Name:</span>{' '}
                      {sponsorContactName}
                    </Typography>
                    <Typography variant="body2" sx={{ mb: 1 }}>
                      <span style={{ fontWeight: 600 }}>Contact Details:</span>{' '}
                      {sponsorContact}
                    </Typography>
                    <Typography variant="body2" sx={{ mb: 1 }}>
                      <span style={{ fontWeight: 600 }}>Request:</span>{' '}
                      {requestDetails}
                    </Typography>
                    <Typography variant="body2" sx={{ mb: 1 }}>
                      <span style={{ fontWeight: 600 }}>Event:</span>{' '}
                      {eventDetails}
                    </Typography>
                    <Typography variant="body2" sx={{ mb: 1 }}>
                      <span style={{ fontWeight: 600 }}>Timeline:</span>{' '}
                      {timeline}
                    </Typography>
                    {valueProposition && (
                      <Typography variant="body2">
                        <span style={{ fontWeight: 600 }}>
                          Value Proposition:
                        </span>{' '}
                        {valueProposition}
                      </Typography>
                    )}
                  </Box>
                )}
              </Box>
            </Collapse>

            {editingLetter ? (
              <Box>
                <FormLabel
                  htmlFor="letter-edit"
                  sx={{ mb: 1, display: 'block' }}
                >
                  Edit Sponsorship Letter
                </FormLabel>
                <TextField
                  id="letter-edit"
                  multiline
                  rows={15}
                  fullWidth
                  value={letterText}
                  onChange={(e) => setLetterText(e.target.value)}
                  aria-label="Letter text - edit mode"
                  inputRef={letterEditRef}
                  sx={{
                    mb: 2,
                    '& .MuiOutlinedInput-notchedOutline': {
                      borderColor: '#757575',
                    },
                  }}
                />
                <Box
                  sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}
                >
                  <Button
                    variant="outlined"
                    onClick={handleCancelLetterEdit}
                    disableRipple
                    sx={{
                      borderColor: '#1C3045',
                      color: '#1C3045',
                      textTransform: 'none',
                      '&:focus': {
                        backgroundColor: '#e8e8e8',
                      },
                    }}
                  >
                    Cancel Edit
                  </Button>
                  <Button
                    variant="contained"
                    onClick={() => setEditingLetter(false)}
                    disableRipple
                    sx={{
                      bgcolor: '#1C3045',
                      textTransform: 'none',
                    }}
                  >
                    Done
                  </Button>
                </Box>
              </Box>
            ) : (
              <Box>
                <Box
                  sx={{
                    whiteSpace: 'pre-wrap',
                    wordBreak: 'break-word',
                    p: 3,
                    borderRadius: 2,
                    backgroundColor: '#f5f5f5',
                    border: '1px solid #e0e0e0',
                    mb: 3,
                    height: '400px',
                    overflow: 'auto',
                    resize: 'vertical',
                    minHeight: '150px',
                    filter: isSubmitting ? 'blur(2px)' : 'none',
                    pointerEvents: isSubmitting ? 'none' : 'auto',
                    transition: !isSubmitting ? 'filter 0.5s ease' : 'none',
                  }}
                >
                  <Typography
                    variant="body1"
                    aria-label="Letter text - view mode"
                  >
                    {letterText}
                  </Typography>
                </Box>
                <Box
                  sx={{
                    display: 'flex',
                    gap: 2,
                    justifyContent: 'space-between',
                  }}
                >
                  <Box sx={{ display: 'flex', gap: 2 }}>
                    <Button
                      variant="outlined"
                      onClick={handleSubmit}
                      disableRipple
                      disabled={isSubmitting}
                      startIcon={
                        isSubmitting ? (
                          <CircularProgress
                            size={20}
                            sx={{ color: 'inherit' }}
                          />
                        ) : (
                          <AutoAwesome />
                        )
                      }
                      sx={{
                        borderColor: '#1C3045',
                        color: '#1C3045',
                        textTransform: 'none',
                        '&:focus': {
                          backgroundColor: '#e8e8e8',
                        },
                      }}
                    >
                      {isSubmitting ? 'Regenerating...' : 'Regenerate'}
                    </Button>
                    {/* TODO LATER: IMPLEMENT SUGGEST CHANGES */}
                    {/* <Button
                      variant="outlined"
                      onClick={() => setShowSuggestChanges(!showSuggestChanges)}
                      disableRipple
                      sx={{
                        borderColor: '#1C3045',
                        color: '#1C3045',
                        textTransform: 'none',
                      }}
                    >
                      Suggest Changes
                    </Button> */}
                  </Box>
                  <Box sx={{ display: 'flex', gap: 2 }}>
                    <Button
                      variant="outlined"
                      onClick={() => {
                        setEditingLetter(true)
                        // Focus the edit field after state update
                        setTimeout(() => {
                          if (letterEditRef.current) {
                            letterEditRef.current.focus()
                          }
                        }, 0)
                      }}
                      disableRipple
                      sx={{
                        borderColor: '#1C3045',
                        color: '#1C3045',
                        textTransform: 'none',
                        '&:focus': {
                          backgroundColor: '#e8e8e8',
                        },
                      }}
                    >
                      Edit Letter
                    </Button>
                    <Button
                      id="export-button"
                      variant="contained"
                      onClick={handleExportMenuOpen}
                      disableRipple
                      disabled={emailing}
                      startIcon={
                        emailing ? (
                          <CircularProgress
                            size={20}
                            sx={{ color: 'inherit' }}
                          />
                        ) : (
                          <GetApp />
                        )
                      }
                      aria-controls={
                        exportMenuAnchor ? 'export-menu' : undefined
                      }
                      aria-haspopup="true"
                      aria-expanded={Boolean(exportMenuAnchor)}
                      aria-label={
                        emailing
                          ? 'Sending...'
                          : 'Export letter in different formats'
                      }
                      sx={{
                        bgcolor: '#1C3045',
                        textTransform: 'none',
                      }}
                    >
                      {emailing ? 'Sending...' : 'Export Letter'}
                    </Button>
                  </Box>
                </Box>
              </Box>
            )}

            {/* Export Menu */}
            <Menu
              id="export-menu"
              anchorEl={exportMenuAnchor}
              open={Boolean(exportMenuAnchor)}
              onClose={handleExportMenuClose}
              anchorOrigin={{
                vertical: 'bottom',
                horizontal: 'right',
              }}
              transformOrigin={{
                vertical: 'top',
                horizontal: 'right',
              }}
              slotProps={{
                paper: {
                  'aria-labelledby': 'export-button',
                  role: 'menu',
                },
              }}
            >
              <MenuItem
                onClick={() => handleExportOption('copy')}
                role="menuitem"
                aria-describedby="copy-description"
              >
                <ListItemIcon>
                  <Description fontSize="small" aria-hidden="true" />
                </ListItemIcon>
                <ListItemText
                  primary="Copy to Clipboard"
                  secondary="Copy letter text for pasting elsewhere"
                />
              </MenuItem>
              {/* TODO LATER: Implement PDF and DOCX export options */}
              {/* <MenuItem
                onClick={() => handleExportOption('pdf')}
                role="menuitem"
                aria-describedby="pdf-description"
              >
                <ListItemIcon>
                  <PictureAsPdf fontSize="small" aria-hidden="true" />
                </ListItemIcon>
                <ListItemText
                  primary="Download as PDF"
                  secondary="Save as PDF document"
                />
              </MenuItem>
              <MenuItem
                onClick={() => handleExportOption('docx')}
                role="menuitem"
                aria-describedby="docx-description"
              >
                <ListItemIcon>
                  <Description fontSize="small" aria-hidden="true" />
                </ListItemIcon>
                <ListItemText
                  primary="Download as DOCX"
                  secondary="Save as editable Word document"
                />
              </MenuItem> */}
              <MenuItem
                onClick={() => handleExportOption('email')}
                role="menuitem"
                aria-describedby="email-description"
              >
                <ListItemIcon>
                  <Email fontSize="small" aria-hidden="true" />
                </ListItemIcon>
                <ListItemText
                  primary="Email to Me"
                  secondary="Send letter to your email address"
                />
              </MenuItem>
            </Menu>

            {/* Suggest Changes Section */}
            {showSuggestChanges && (
              <Box sx={{ mt: 3 }}>
                <FormControl sx={{ width: '100%' }}>
                  <Typography
                    variant="caption"
                    sx={{ mb: 1, display: 'block' }}
                  >
                    Provide details of the changes you would like to make to the
                    letter to improve it, then click 'Regenerate with Feedback'.
                  </Typography>
                  <Typography
                    variant="caption"
                    sx={{ mb: 2, display: 'block' }}
                  >
                    When you're happy with the letter, click 'Copy Letter'
                    above. If you want to discard the changes, click 'Cancel'.
                  </Typography>
                  <TextField
                    multiline
                    rows={6}
                    sx={{
                      width: '100%',
                      mb: 2,
                      '& .MuiOutlinedInput-notchedOutline': {
                        borderColor: '#757575',
                      },
                    }}
                    value={suggestionFeedback}
                    onChange={(e) => setSuggestionFeedback(e.target.value)}
                    placeholder="Describe what you'd like changed in the letter..."
                    autoComplete="off"
                    autoFocus
                    aria-label="Suggestion feedback - describe changes you want to make to the letter"
                  />
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'center',
                      alignItems: 'center',
                      gap: 2,
                    }}
                  >
                    <Button
                      variant="outlined"
                      onClick={() => {
                        setShowSuggestChanges(false)
                        setSuggestionFeedback('')
                      }}
                      disableRipple
                      sx={{
                        textTransform: 'none',
                        color: '#1C3045',
                        borderColor: '#1C3045',
                        border: 2,
                        '&:focus': {
                          backgroundColor: '#e8e8e8',
                        },
                      }}
                    >
                      Cancel
                    </Button>
                    <Button
                      sx={{
                        textTransform: 'none',
                        color: '#1C3045',
                        minWidth: '250px',
                        justifyContent: 'center',
                        fontSize: '16px',
                        borderColor: '#1C3045',
                        border: 2,
                        '&:focus': {
                          backgroundColor: '#e8e8e8',
                        },
                      }}
                      variant="outlined"
                      disableRipple
                      disabled={isSubmitting}
                      startIcon={
                        isSubmitting ? (
                          <CircularProgress
                            size={20}
                            sx={{ color: 'inherit' }}
                          />
                        ) : (
                          <AutoAwesome />
                        )
                      }
                      onClick={() => {
                        // TODO LATER: Implement regenerate with feedback
                        // console.log(
                        //   'Regenerating with feedback:',
                        //   suggestionFeedback
                        // )
                      }}
                    >
                      {isSubmitting
                        ? 'Regenerating...'
                        : 'Regenerate with Feedback'}
                    </Button>
                  </Box>
                </FormControl>
              </Box>
            )}
          </Box>
        )}

        {/* Letter Edit Cancel Confirmation */}
        {showLetterEditCancelConfirmation && editingLetter && (
          <Alert
            severity="warning"
            sx={{ mb: 2 }}
            role="alert"
            aria-live="assertive"
            tabIndex={-1}
            ref={letterEditCancelWarningRef}
          >
            <Typography variant="body2" sx={{ mb: 2 }}>
              You have unsaved changes to your letter that will be lost. Are you
              sure you want to cancel editing?
            </Typography>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button
                size="small"
                variant="outlined"
                onClick={handleKeepEditingLetter}
                disableRipple
                sx={{
                  textTransform: 'none',
                  '&:focus': {
                    backgroundColor: '#e8e8e8',
                  },
                }}
              >
                Keep Editing
              </Button>
              <Button
                size="small"
                variant="contained"
                onClick={handleDiscardLetterChanges}
                disableRipple
                sx={{
                  textTransform: 'none',
                }}
              >
                Discard Changes
              </Button>
            </Box>
          </Alert>
        )}

        {/* Input Form Cancel Confirmation */}
        {showInputFormCancelConfirmation && !generatedLetter && (
          <Alert
            severity="warning"
            sx={{ mb: 2 }}
            ref={inputFormWarningRef}
            role="alert"
            aria-live="assertive"
            tabIndex={-1}
          >
            <Typography variant="body2" sx={{ mb: 2 }}>
              You have entered some information that will be lost. Are you sure
              you want to cancel?
            </Typography>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button
                size="small"
                variant="outlined"
                onClick={() => setShowInputFormCancelConfirmation(false)}
                disableRipple
                sx={{
                  textTransform: 'none',
                  '&:focus': {
                    backgroundColor: '#e8e8e8',
                  },
                }}
              >
                Keep Editing
              </Button>
              <Button
                size="small"
                variant="contained"
                onClick={handleConfirmedClose}
                disableRipple
                sx={{
                  textTransform: 'none',
                  '&:focus': {
                    backgroundColor: '#e8e8e8',
                  },
                }}
              >
                Discard & Close
              </Button>
            </Box>
          </Alert>
        )}

        {/* Input Form - only show if no letter generated */}
        {!generatedLetter && (
          <form onSubmit={handleSubmit}>
            <FormControl sx={{ width: '100%', mb: 2 }}>
              <Typography
                variant="subtitle2"
                sx={{ mb: 1 }}
                component="label"
                htmlFor="sponsor-name-input"
              >
                Sponsor Name *
              </Typography>
              <TextField
                id="sponsor-name-input"
                fullWidth
                value={sponsorName}
                onChange={(e) => setSponsorName(e.target.value)}
                disabled={isSubmitting}
                placeholder="Enter sponsor/company name"
                autoComplete="off"
                required
                slotProps={{
                  input: {
                    'aria-describedby': 'sponsor-name-helper',
                  },
                }}
                sx={{
                  mb: 2,
                  '& .MuiOutlinedInput-notchedOutline': {
                    borderColor: '#757575',
                  },
                }}
              />

              <Typography
                variant="subtitle2"
                sx={{ mb: 1 }}
                component="label"
                htmlFor="sponsor-contact-name-input"
              >
                Sponsor Contact Name *
              </Typography>
              <TextField
                id="sponsor-contact-name-input"
                fullWidth
                multiline
                rows={2}
                value={sponsorContactName}
                onChange={(e) => setSponsorContactName(e.target.value)}
                disabled={isSubmitting}
                placeholder="Contact person name"
                autoComplete="off"
                required
                sx={{
                  mb: 2,
                  '& .MuiOutlinedInput-notchedOutline': {
                    borderColor: '#757575',
                  },
                }}
              />
              <Typography
                variant="subtitle2"
                sx={{ mb: 1 }}
                component="label"
                htmlFor="sponsor-contact-input"
              >
                Sponsor Contact Details (Optional)
              </Typography>
              <TextField
                id="sponsor-contact-input"
                fullWidth
                multiline
                rows={2}
                value={sponsorContact}
                onChange={(e) => setSponsorContact(e.target.value)}
                disabled={isSubmitting}
                placeholder="Email, phone, address, etc."
                autoComplete="off"
                slotProps={{
                  input: {
                    'aria-describedby': 'sponsor-contact-helper',
                  },
                }}
                sx={{
                  mb: 2,
                  '& .MuiOutlinedInput-notchedOutline': {
                    borderColor: '#757575',
                  },
                }}
              />

              <Typography
                variant="subtitle2"
                sx={{ mb: 1 }}
                component="label"
                htmlFor="request-details-input"
              >
                Request Details *
              </Typography>
              <Typography
                id="request-details-helper"
                variant="body2"
                sx={{ mb: 1, color: 'text.secondary' }}
              >
                What are you requesting? (monetary amount, in-kind items,
                services)
              </Typography>
              <SpeechToText
                token={token}
                disabled={isSubmitting}
                onTranscription={(text) =>
                  setRequestDetails((prev) => prev ? `${prev}\n\n${text}` : text)
                }
              />
              <TextField
                id="request-details-input"
                fullWidth
                multiline
                rows={3}
                value={requestDetails}
                onChange={(e) => setRequestDetails(e.target.value)}
                disabled={isSubmitting}
                placeholder="e.g., $5,000 cash sponsorship, catering for 100 people, printing services..."
                autoComplete="off"
                required
                slotProps={{
                  input: {
                    'aria-describedby': 'request-details-helper',
                  },
                }}
                sx={{
                  mb: 2,
                  '& .MuiOutlinedInput-notchedOutline': {
                    borderColor: '#757575',
                  },
                  '& textarea': {
                    resize: 'vertical',
                  },
                }}
              />

              <Typography
                variant="subtitle2"
                sx={{ mb: 1 }}
                component="label"
                htmlFor="event-details-input"
              >
                Event/Program Details *
              </Typography>
              <Typography
                id="event-details-helper"
                variant="body2"
                sx={{ mb: 1, color: 'text.secondary' }}
              >
                What event or program does this sponsorship support?
              </Typography>
              <SpeechToText
                token={token}
                disabled={isSubmitting}
                onTranscription={(text) =>
                  setEventDetails((prev) => prev ? `${prev}\n\n${text}` : text)
                }
              />
              <TextField
                id="event-details-input"
                fullWidth
                multiline
                rows={3}
                value={eventDetails}
                onChange={(e) => setEventDetails(e.target.value)}
                disabled={isSubmitting}
                placeholder="Event name, date, location, purpose, expected attendance..."
                autoComplete="off"
                required
                slotProps={{
                  input: {
                    'aria-describedby': 'event-details-helper',
                  },
                }}
                sx={{
                  mb: 2,
                  '& .MuiOutlinedInput-notchedOutline': {
                    borderColor: '#757575',
                  },
                  '& textarea': {
                    resize: 'vertical',
                  },
                }}
              />

              <Typography
                variant="subtitle2"
                sx={{ mb: 1 }}
                component="label"
                htmlFor="timeline-input"
              >
                Timeline/Deadline *
              </Typography>
              <TextField
                id="timeline-input"
                fullWidth
                value={timeline}
                onChange={(e) => setTimeline(e.target.value)}
                disabled={isSubmitting}
                placeholder="When do you need a response by?"
                autoComplete="off"
                required
                slotProps={{
                  input: {
                    'aria-describedby': 'timeline-helper',
                  },
                }}
                sx={{
                  mb: 2,
                  '& .MuiOutlinedInput-notchedOutline': {
                    borderColor: '#757575',
                  },
                }}
              />

              <Typography
                variant="subtitle2"
                sx={{ mb: 1 }}
                component="label"
                htmlFor="value-proposition-input"
              >
                Value Proposition (Optional)
              </Typography>
              <Typography
                id="value-proposition-helper"
                variant="body2"
                sx={{ mb: 1, color: 'text.secondary' }}
              >
                What's in it for the sponsor? (advertising, tax benefits,
                community impact, etc.)
              </Typography>
              <SpeechToText
                token={token}
                disabled={isSubmitting}
                onTranscription={(text) =>
                  setValueProposition((prev) => prev ? `${prev}\n\n${text}` : text)
                }
              />
              <TextField
                id="value-proposition-input"
                fullWidth
                multiline
                rows={3}
                value={valueProposition}
                onChange={(e) => setValueProposition(e.target.value)}
                disabled={isSubmitting}
                placeholder="Logo placement, social media mentions, tax deduction receipt, community recognition..."
                autoComplete="off"
                slotProps={{
                  input: {
                    'aria-describedby': 'value-proposition-helper',
                  },
                }}
                sx={{
                  mb: 3,
                  '& .MuiOutlinedInput-notchedOutline': {
                    borderColor: '#757575',
                  },
                  '& textarea': {
                    resize: 'vertical',
                  },
                }}
              />
            </FormControl>

            <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
              <Button
                variant="outlined"
                disableRipple
                onClick={handleInputFormCancel}
                disabled={isSubmitting}
                sx={{
                  borderColor: '#1C3045',
                  border: 2,
                  borderRadius: 2,
                  color: '#1C3045',
                  textTransform: 'none',
                  fontSize: '16px',
                  '&:focus': {
                    backgroundColor: '#e8e8e8',
                  },
                }}
              >
                Cancel
              </Button>
              <Button
                disableRipple
                variant="contained"
                type="submit"
                disabled={
                  isSubmitting ||
                  !sponsorName.trim() ||
                  !sponsorContactName.trim() ||
                  !requestDetails.trim() ||
                  !eventDetails.trim() ||
                  !timeline.trim()
                }
                startIcon={
                  isSubmitting ? (
                    <CircularProgress size={20} sx={{ color: 'inherit' }} />
                  ) : undefined
                }
                sx={{
                  bgcolor: '#1C3045',
                  borderRadius: 2,
                  textTransform: 'none',
                  fontSize: '16px',
                }}
              >
                {isSubmitting ? 'Generating...' : 'Generate Letter'}
              </Button>
            </Box>
          </form>
        )}

        {/* Copy Success Snackbar */}
        <Snackbar
          open={showCopySuccess}
          autoHideDuration={3000}
          onClose={() => setShowCopySuccess(false)}
          message="Letter copied to clipboard!"
          anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
          aria-label="Copy success notification"
        />
        {/* Email Success Snackbar */}
        <Snackbar
          open={showEmailSuccess}
          autoHideDuration={3000}
          onClose={() => setShowEmailSuccess(false)}
          message="Letter has been emailed to you!"
          anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
          aria-label="Email send success notification"
        />
        {/* Email Error Snackbar */}
        <Snackbar
          open={showEmailError}
          autoHideDuration={3000}
          onClose={() => setShowEmailError(false)}
          message="Sorry, something went wrong sending the email!"
          anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
          aria-label="Email send error notification"
        />
      </Box>
    </Modal>
  )
}

export default GenerateSponsorshipLetter
